// @flow

import { useEffect, useCallback, useRef } from 'react';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import * as WidgetPropsModel from '../../../models/widget-props';
import WidgetUI from '../../widgets-ui/calendar-heatmap';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import { processCalendarHeatmapWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/calendar-heatmap';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const CalendarHeatmapWidget = ({
  widget,
  availableMetrics,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.calendarHeatmapT) => {
  const [loading, setLoading] = useSafeState(false);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [widgetDescription, setWidgetDescription] = useSafeState('');
  const [tableRows, setTableRows] = useSafeState([]);
  const [unitType, setUnitType] = useSafeState('number');
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible((vi) => uiOptions.disableViewportLoading || vi > 0);
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);

  const title = widget.customTitle || autoTitle;

  const loadWidgetData = async metric => {
    setLoading(true);
    setError(false);
    reportLoading();

    try {
      const config = {
        metric: widget.metric,
        period: widget.period,
        locations: widget.locations,
        demographicFilter: widget.demographicFilter,
        ...(widget.customTitle && { customTitle: widget.customTitle }),
      };

      const deps = {
        queryAPI: browserQueryAPI,
        availableMetrics: availableMetrics,
        locations: [], // Not used by calendar heatmap widget
        startOfWeek: 'monday', // Default to Monday, can be injected from org settings
      };

      const widgetData = await processCalendarHeatmapWidget(config, deps);

      // Extract title from package
      setAutoTitle(widgetData.title);

      // Map WidgetData back to tableRows format
      // widgetData.rows is array of arrays: [weekStart, mon, tue, wed, thu, fri, sat, sun]
      // Need to convert to format: { label: weekStart, cols: [{name: 'mon', value: x}, ...] }
      const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      const builtRows = widgetData.rows.map(row => {
        const weekStart = row[0].value; // First cell is the week start
        const cols = dayKeys.map((dayKey, index) => ({
          name: dayKey,
          value: row[index + 1].value, // +1 because first cell is week start
        }));
        return { label: weekStart, cols };
      });

      setTableRows(builtRows);

      // Get unit type from first data cell (skip week start column)
      if (widgetData.rows.length > 0 && widgetData.rows[0].length > 1) {
        setUnitType(widgetData.rows[0][1].unit);
      }

      // Set description
      if (!uiOptions.disableDescription) {
        const demos = Object.values(metric.demographicFilter ?? {}).filter(Boolean).join(" ");
        const desc = AvailableMetricsModel.getMetricDescription(metric.details);
        setWidgetDescription(demos.length ? `${desc} (${demos})` : desc);
      }

      // Report completion to context - use package output directly
      reportComplete(widgetData);

      setLoading(false);
    } catch (e) {
      setError(true);
      setLoading(false);
      reportError(e.message || 'Failed to load Calendar Heatmap widget');
      console.error('API call for Calendar Heatmap widget failed', e);
    }
  };

  const [fetchRequired, setFetchRequired] = useSafeState(true);
  const hasInitialFetchedRef = useRef(false);

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;

      const metric = {
        ...widget.metric, // Metric-level properties
        demographicFilter: widget.metric.demographicFilter || widget.demographicFilter, // Metric-level takes precedence, fallback to widget-level
        details: AvailableMetricsModel.findMetricDetails(
          availableMetrics,
          widget.metric.metricGroupKey,
          widget.metric.metricKey
        )
      };

      if (!metric || !metric.details) {
        console.error(
          'No metric found in availableMetrics for the Calendar Heatmap widget'
        );
        setError(true);
      } else {
        loadWidgetData(metric);
      }
    }
  }, [widget, fetchRequired, isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtitle = buildWidgetDateString(widget);

  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        title={title}
        description={widgetDescription}
        subtitle={subtitle}
        unitType={unitType}
        tableRows={tableRows}
      />
    </div>
  );
};

export default CalendarHeatmapWidget;
